import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useLead } from '../contexts/LeadContext';
import { PhotoPlaceholder } from './PhotoPlaceholder';
import { HERO_PHOTO } from '../data/photos';
import { trackHeroCTA } from '../tracking';
import { scrollToLeadForm } from '../utils/scroll';
export function Hero() {
  const {
    t
  } = useLanguage();
  const {
    openPicker
  } = useLead();
  const reduce = useReducedMotion();
  const chips = ['hero.chip1', 'hero.chip2', 'hero.chip3', 'hero.chip4'];
  const rise = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 15
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.6,
      delay,
      ease: [0.22, 1, 0.36, 1] as const
    }
  };

  // The headline's final word carries the gold underline — the line itself stays Midnight.
  const headlineWords = t('hero.headline2').split(' ');
  const lastWord = headlineWords.pop() ?? '';
  const leadWords = headlineWords.join(' ');
  return <section id="top" className="w-full bg-white" data-id="element-84" data-mp="3j6fy7px5bddu">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-stretch lg:min-h-[88svh] lg:grid-cols-2" data-id="element-85" data-mp="3o7vjnj38jlwr">
        {/* Mobile: photo first, copy below. Desktop: copy left, photo right. */}
        <div className="order-1 h-[32svh] w-full pt-14 sm:h-[44svh] lg:order-2 lg:h-auto lg:pt-0" data-id="element-86" data-mp="3e8whmjcz6oiq">
          {/* No overlay, no darkening, no duotone — the photograph stands on its own.
              Hero image is eager with high fetch priority; every other photo is lazy. */}
          {HERO_PHOTO ? <img src={HERO_PHOTO} alt={t('hero.photoAlt')} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full bg-offwhite object-cover object-top" data-id="element-87" data-mp="2qkmu9d045sph" /> : <PhotoPlaceholder label={t('hero.photo')} tone="warm" className="border-y-0 lg:border-l" data-id="element-88" data-mp="2lla5gmpombk9" />}
        </div>

        <div className="order-2 flex flex-col justify-center px-4 py-8 sm:px-6 sm:py-10 lg:order-1 lg:pb-20 lg:pr-14 lg:pt-28" data-id="element-89" data-mp="nbba6qtxtx1l">
          <motion.p {...rise(0)} className="font-display text-[10px] font-bold uppercase leading-relaxed tracking-[0.24em] text-camelink sm:text-xs" data-id="element-90" data-mp="ibgjana5x7gj">
            {t('hero.eyebrow')}
          </motion.p>
          <motion.div {...rise(0.05)} aria-hidden="true" className="mt-3 h-[3px] w-12 bg-kred" data-id="element-91" data-mp="1b049mx9rqn0y" />

          <motion.h1 {...rise(0.1)} className="mt-5 font-display text-[2.5rem] font-extrabold uppercase leading-[0.95] tracking-[-0.02em] text-ink sm:text-6xl" data-id="element-92" data-mp="162lyd08a3vps">
            {t('hero.headline1')}
            <br data-id="element-93" data-mp="3lsypkqaae6eu" />
            {leadWords ? `${leadWords} ` : ''}
            <span className="relative inline-block" data-id="element-94" data-mp="3lu9yf4ila4ct">
              <span aria-hidden="true" className="absolute inset-x-0 bottom-[0.08em] h-[4px] bg-gold" data-id="element-95" data-mp="3hr3tc077qj7x" />
              <span className="relative" data-id="element-96" data-mp="1x783xkq1jlsj">{lastWord}</span>
            </span>
          </motion.h1>

          <motion.p {...rise(0.2)} className="mt-5 max-w-xl text-base leading-relaxed text-body sm:text-lg" data-id="element-97" data-mp="218to7r4wn2cr">
            {t('hero.sub')}
          </motion.p>

          <motion.div {...rise(0.3)} className="mt-7 flex w-full flex-col gap-3 sm:max-w-lg sm:flex-row" data-id="element-98" data-mp="2b1rbeakudfhp">
            {/* The single gold-filled CTA in this viewport. */}
            <button type="button" onClick={() => {
            trackHeroCTA();
            scrollToLeadForm();
          }} className="min-h-[52px] flex-1 bg-gold px-6 text-sm font-bold uppercase tracking-[0.12em] text-golddeep transition-transform hover:-translate-y-[1px]" data-id="element-99" data-mp="2kyn9rnh10l8j">
              {t('hero.ctaPrimary')}
            </button>
            <button type="button" onClick={() => openPicker('hero')} className="min-h-[52px] flex-1 border border-ink bg-transparent px-6 text-sm font-bold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-ink hover:text-white" data-id="element-100" data-mp="zlaqw2ufk8nz">
              {t('hero.ctaSecondary')}
            </button>
          </motion.div>

          {/* Plain text separated by a camel dot — no boxes, nothing that looks tappable. */}
          <motion.p {...rise(0.4)} className="mt-7 text-sm leading-relaxed text-muted" data-id="element-101" data-mp="98mi3pan4tu3">
            {chips.map((chip, index) => <React.Fragment key={chip} data-id="element-102" data-mp="3crpijgt639pq" data-mpi={index}>
                {index > 0 && <span aria-hidden="true" className="mx-2 text-camel" data-id="element-103" data-mp="3rg3jfm70wo3n">
                    ·
                  </span>}
                {t(chip)}
              </React.Fragment>)}
          </motion.p>

          <motion.p {...rise(0.5)} className="mt-8 font-display text-xs font-bold uppercase tracking-[0.26em] text-ink/70" data-id="element-104" data-mp="2yyzkuf4a0gcs">
            {t('hero.tagline')}
          </motion.p>
        </div>
      </div>
    </section>;
}